import {View, Text, StyleSheet} from 'react-native';
import {useTranslation} from 'react-i18next';
import {Image} from 'expo-image';
import {useCurrentTheme} from "@/theming/useCurrentTheme";
import {ThemeType} from "@/theming/baseTheme";
import type {AppDataAchievement} from '@/data/app-data-interfaces';
import {AchievementContent} from "@/components/achievement/AchievementContent";
import {BaseCard} from "@/components/common/BaseCard";
import {ResponsiveGrid} from "@/components/common/ResponsiveGrid";

interface OfferAchievementsSectionProps {
    achievements: AppDataAchievement[];
}

export const OfferAchievementsSection = ({achievements}: OfferAchievementsSectionProps) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const styles = createStyles(theme);

    if (!achievements || achievements.length === 0) {
        return null;
    }

    return (
        <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t('offers.includedAchievements')}</Text>
            <ResponsiveGrid data={achievements}
                            gap={theme.spacing.xs}
                            maxItemWidth={600}
                            minItemWidth={400}
                            scrollEnabled={false}
                            renderItem={a => <BaseCard variant={"default"} outlined={true}
                                                       style={styles.achievementCard}>
                                <AchievementContent achievement={a} size="md"/>
                            </BaseCard>}
                            keyExtractor={(a => a.id)}/>
        </View>
    );
};

const createStyles = (theme: ThemeType) => StyleSheet.create({
    section: {
        marginVertical: 20,
    },
    sectionTitle: {
        fontSize: 18,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: 12,
    },
    achievementsList: {
        gap: 12,
    },
    achievementCard: {
        paddingHorizontal: theme.spacing.md
    },
    achievementItem: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 16,
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        borderWidth: 1,
        borderColor: theme.colors.border,
    },
    achievementIcon: {
        width: 48,
        height: 48,
        backgroundColor: theme.colors.secondary,
        borderRadius: 24,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: 12,
        overflow: 'hidden',
    },
    achievementIconImage: {
        width: 32,
        height: 32,
    },
    achievementContent: {
        flex: 1,
    },
    achievementTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.text,
        marginBottom: 4,
    },
    achievementDescription: {
        fontSize: 14,
        color: theme.colors.textSecondary,
        lineHeight: 20,
    },
});
